// Pure helpers for useActivityTracker. DOM helpers operate on Element
// instances and are jsdom-testable; getSupabaseSession reads localStorage
// + import.meta.env, which jsdom also provides via the test environment.

export const FLUSH_INTERVAL_MS = 5_000
export const RAGE_CLICK_THRESHOLD = 3
export const RAGE_CLICK_WINDOW_MS = 500

export function generateUUID(): string {
  return crypto.randomUUID()
}

// Reads the Supabase access token + user id from the auth session that
// the @appic/service-layer client persists in localStorage at
// 'sb-clanda-sl' (see packages/service-layer/src/supabaseClient.ts —
// storageKey is set there). Returns null when:
//   - the session key is absent (logged out, or setSession never ran)
//   - the JSON is malformed (corrupted localStorage)
//   - either field is missing on the parsed session
//
// Prior versions read `sb-${ref}-auth-token` (the supabase-js DEFAULT
// key) which our clients NEVER write to — they all set explicit
// storageKeys. That bug silently dropped every activity-tracker flush
// on every platform (BUG-SL-SCOUT-REP-CANNOT-READ-OWN-MISSIONS-2026-05-26).
//
// Logs malformed-JSON via console.warn — silent JSON.parse failures used
// to mask real auth-session corruption bugs (D-33: no silent failures).
export const SL_AUTH_STORAGE_KEY = 'sb-clanda-sl'

export function getSupabaseSession(): { accessToken: string; userId: string } | null {
  const raw = localStorage.getItem(SL_AUTH_STORAGE_KEY)
  if (!raw) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (err) {
    // Telemetry must not crash the app, but the operator needs to know
    // the auth-session JSON in localStorage is corrupted.
    console.warn('[activity-tracker] failed to parse Supabase auth-token JSON', err)
    return null
  }

  const session = parsed as { access_token?: string; user?: { id?: string } } | null
  const accessToken = session?.access_token
  const userId = session?.user?.id
  if (!accessToken || !userId) return null
  return { accessToken, userId }
}

// Compact CSS selector for an element — used in telemetry payloads.
// Format: tag[#id][.cls1.cls2] (max 2 classes to bound payload size).
export function compactSelector(el: Element): string {
  const tag = el.tagName.toLowerCase()
  const id = el.id ? `#${el.id}` : ''
  const cls = el.className && typeof el.className === 'string'
    ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
    : ''
  return `${tag}${id}${cls}`
}

// Whether the element is interactive by design — anchor, button, input,
// role=button, has tabindex, has data-track, or is nested inside one.
// Used to distinguish dead clicks (non-interactive) from real clicks.
export function isInteractive(el: Element): boolean {
  const tag = el.tagName.toLowerCase()
  if (['a', 'button', 'input', 'select', 'textarea', 'summary'].includes(tag)) return true
  if (el.getAttribute('role') === 'button') return true
  if (el.getAttribute('tabindex') !== null) return true
  if (el.hasAttribute('data-track')) return true
  if (el.closest('a, button, [role="button"], [data-track]')) return true
  return false
}
